import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import {
  LayoutDashboard, Wallet, FileSpreadsheet, Download, Loader2, Search,
  AlertCircle, IndianRupee, FileText, CheckCircle2, Clock, TrendingUp, X,
  User, Calendar, Sparkles, ClipboardList, Filter,
} from 'lucide-react';

const fmtINR = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

function KpiTile({ label, value, sub, icon: Icon, accent, testid }) {
  const accents = {
    teal:   'from-teal-500/20 to-teal-500/5 border-teal-500/30 text-teal-300',
    emerald:'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300',
    amber:  'from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-300',
    sky:    'from-sky-500/20 to-sky-500/5 border-sky-500/30 text-sky-300',
  };
  return (
    <div data-testid={testid} className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${accents[accent]} p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">{label}</p>
          <p className="mt-2 text-2xl sm:text-3xl font-bold text-white">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-400">{sub}</p>}
        </div>
        <div className="p-2.5 rounded-xl bg-slate-900/60 border border-slate-800"><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
}

function DateFilter({ preset, setPreset, from, setFrom, to, setTo }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex items-center bg-slate-900/60 border border-slate-800 rounded-xl p-1" data-testid="marketing-preset-toggle">
        {PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            data-testid={`marketing-preset-${p.key}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${preset === p.key ? 'bg-teal-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === 'custom' && (
        <>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="marketing-from-date"
              className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-sm text-white" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="marketing-to-date"
              className="px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-sm text-white" />
          </div>
        </>
      )}
    </div>
  );
}

function ReferralDetailModal({ referralId, onClose, getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/marketing/my-referrals/${referralId}`, { headers: getAuthHeader() });
        if (alive) setData(res.data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'Failed to load referral.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [referralId, getAuthHeader]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl mt-8 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl" data-testid="marketing-referral-modal">
        <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-teal-400 font-mono">{data?.referral_id || '…'}</div>
            <h2 className="text-xl font-bold text-white mt-0.5">{data?.patient_name || 'Loading…'}</h2>
            {data && (
              <div className="text-xs text-slate-400 mt-1 space-x-2">
                <span>OP: {data.op_number}</span>
                <span>· {data.referral_date}</span>
                <span>· {data.village || '—'}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="marketing-modal-close" className="p-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        {loading && <div className="p-16 text-center"><Loader2 className="w-6 h-6 text-teal-400 animate-spin mx-auto" /></div>}
        {err && <div className="p-8 text-center text-rose-300 text-sm">{err}</div>}
        {data && !loading && (
          <div className="p-5 space-y-4">
            <SectionCard title="Patient">
              <KV k="Doctor" v={data.referral_doctor_name || '—'} />
              <KV k="Consultant" v={data.consultant_name || '—'} />
            </SectionCard>
            <SectionCard title="Scan">
              <KV k="Total" v={`₹${fmtINR(data.scan_total_amount)}`} />
              <KV k="Referral" v={`₹${fmtINR(data.scan_referral_amount)}`} highlight />
            </SectionCard>
            <SectionCard title="Laboratory">
              <KV k="Total" v={`₹${fmtINR(data.lab_total_amount)}`} />
              <KV k="Kit Cost" v={`₹${fmtINR(data.lab_kit_amount)}`} />
              <KV k="Net" v={`₹${fmtINR(data.lab_net_amount)}`} />
              <KV k="Referral" v={`₹${fmtINR(data.lab_referral_amount)}`} highlight />
            </SectionCard>
            <SectionCard title="Pharmacy">
              <KV k="Total" v={`₹${fmtINR(data.pharmacy_total_amount)}`} />
              <KV k="Kit Cost" v={`₹${fmtINR(data.pharmacy_kit_amount)}`} />
              <KV k="Net" v={`₹${fmtINR(data.pharmacy_net_amount)}`} />
              <KV k="Referral" v={`₹${fmtINR(data.pharmacy_referral_amount)}`} highlight />
            </SectionCard>
            <SectionCard title="IP">
              <KV k="Amount" v={`₹${fmtINR(data.ip_amount)}`} />
              <KV k="Deductibles" v={`₹${fmtINR(data.ip_deductibles)}`} />
              <KV k="Net" v={`₹${fmtINR(data.ip_net_amount)}`} />
              <KV k="Referral" v={`₹${fmtINR(data.ip_referral_amount)}`} highlight />
            </SectionCard>
            {(data.procedures || []).length > 0 && (
              <SectionCard title="Procedures" full>
                <div className="col-span-2 w-full overflow-hidden rounded-xl border border-slate-800">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-900 text-slate-400 uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="text-left px-3 py-2">Procedure</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-right px-3 py-2">Referral</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/70">
                      {data.procedures.map(p => (
                        <tr key={p.id}>
                          <td className="px-3 py-2 text-slate-200">{p.procedure_name}</td>
                          <td className="px-3 py-2 text-right font-mono">₹{fmtINR(p.procedure_amount)}</td>
                          <td className="px-3 py-2 text-right font-mono text-teal-300">₹{fmtINR(p.referral_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            )}
            <div className="p-5 rounded-2xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-teal-400 font-semibold">Total Referral Amount</div>
                <div className="text-3xl font-bold text-white font-mono mt-1" data-testid="marketing-modal-final">₹{fmtINR(data.final_referral_amount)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Payment</div>
                <div className={`text-sm font-semibold ${data.payment_status === 'paid' ? 'text-emerald-300' : 'text-amber-300'}`}>{data.payment_status.toUpperCase()}</div>
                {data.payment_date && <div className="text-[10px] text-slate-500 mt-1">{data.payment_date}</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function SectionCard({ title, children, full }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="text-[11px] uppercase tracking-widest text-teal-400 font-semibold mb-3">{title}</div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm`}>{children}</div>
    </div>
  );
}
function KV({ k, v, highlight }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-xs text-slate-500">{k}</span>
      <span className={`font-mono ${highlight ? 'text-teal-300 font-semibold' : 'text-slate-200'}`}>{v}</span>
    </div>
  );
}

export default function MarketingDashboardPage() {
  const { getAuthHeader, user } = useAuth();
  const [tab, setTab] = useState('dashboard'); // dashboard | referrals | payments | reports
  const [preset, setPreset] = useState('month');
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  const [meInfo, setMeInfo] = useState(null); // { linked, executive }
  const [dashboard, setDashboard] = useState(null);
  const [refs, setRefs] = useState([]);
  const [q, setQ] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all'); // all | paid | pending
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openRefId, setOpenRefId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (preset && preset !== 'custom') p.set('preset', preset);
    if (preset === 'custom') { if (from) p.set('from', from); if (to) p.set('to', to); }
    return p;
  }, [preset, from, to]);

  const loadMe = async () => {
    try {
      const r = await axios.get(`${API_BASE}/marketing/me`, { headers: getAuthHeader() });
      setMeInfo(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load profile.');
    }
  };

  const loadDashboard = async () => {
    try {
      setError('');
      setLoading(true);
      const r = await axios.get(`${API_BASE}/marketing/my-dashboard?${params.toString()}`, { headers: getAuthHeader() });
      setDashboard(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  const loadReferrals = async () => {
    try {
      setError('');
      setLoading(true);
      const p = new URLSearchParams(params);
      if (paymentFilter !== 'all') p.set('payment_status', paymentFilter);
      if (q) p.set('q', q);
      const r = await axios.get(`${API_BASE}/marketing/my-referrals?${p.toString()}`, { headers: getAuthHeader() });
      setRefs(r.data.referrals || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load referrals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMe(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (tab === 'dashboard') loadDashboard();
    else if (tab === 'referrals' || tab === 'payments') loadReferrals();
    // eslint-disable-next-line
  }, [tab, preset, from, to, paymentFilter, q]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const p = new URLSearchParams(params);
      if (paymentFilter !== 'all') p.set('payment_status', paymentFilter);
      const res = await axios.get(`${API_BASE}/marketing/my-report.xlsx?${p.toString()}`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `RMS_MarketingExec_${todayISO()}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to download report.');
    } finally {
      setDownloading(false);
    }
  };

  // Split refs into pending/paid for Payments tab
  const paymentSplit = useMemo(() => ({
    pending: refs.filter(r => r.payment_status !== 'paid'),
    paid: refs.filter(r => r.payment_status === 'paid'),
  }), [refs]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal-400 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Marketing Executive Workspace
            </div>
            <h1 data-testid="marketing-page-title" className="mt-1 text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {meInfo?.linked ? `${meInfo.executive.name}` : (user?.name || 'My Referrals')}
            </h1>
            {meInfo?.linked ? (
              <p className="text-sm text-slate-400 mt-1">Village/Area: <span className="text-slate-200">{meInfo.executive.village_area || '—'}</span></p>
            ) : (
              <p className="text-sm text-amber-400 mt-1" data-testid="marketing-not-linked">No Marketing Executive linked to your account — please contact your administrator.</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2" data-testid="marketing-tabs">
          {[
            { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { key: 'referrals', label: 'My Referrals', icon: FileText },
            { key: 'payments', label: 'Payments', icon: Wallet },
            { key: 'reports', label: 'Reports', icon: FileSpreadsheet },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`marketing-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                tab === t.key ? 'bg-teal-500 text-slate-950' : 'bg-slate-900/60 border border-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Date filter (shared across tabs) */}
        {meInfo?.linked && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-teal-400" />
              <span className="text-xs font-medium text-slate-300">Date Range</span>
            </div>
            <DateFilter preset={preset} setPreset={setPreset} from={from} setFrom={setFrom} to={to} setTo={setTo} />
          </div>
        )}

        {error && (
          <div className="p-3 bg-rose-950/60 border border-rose-800 rounded-xl text-rose-200 text-sm flex items-start gap-2" data-testid="marketing-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {!meInfo?.linked && meInfo && (
          <div className="p-16 text-center bg-slate-900/60 border border-slate-800 rounded-2xl">
            <ClipboardList className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-300">No Marketing Executive is linked to your account.</p>
            <p className="text-xs text-slate-500 mt-1">Ask your administrator to link you to your Executive record.</p>
          </div>
        )}

        {meInfo?.linked && tab === 'dashboard' && (
          <>
            {loading && !dashboard && <div className="p-16 text-center text-slate-400 text-sm"><Loader2 className="w-6 h-6 text-teal-400 animate-spin mx-auto" /></div>}
            {dashboard && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="marketing-kpi-grid">
                <KpiTile testid="marketing-kpi-total-count" label="Total Referrals" value={dashboard.totals.count} icon={FileText} accent="teal" />
                <KpiTile testid="marketing-kpi-total-amount" label="Total Referral Amount" value={`₹${fmtINR(dashboard.totals.amount)}`} icon={IndianRupee} accent="emerald" />
                <KpiTile testid="marketing-kpi-paid" label="Paid" value={`₹${fmtINR(dashboard.totals.paid_amount)}`} sub={`${dashboard.totals.paid_count} cleared`} icon={CheckCircle2} accent="sky" />
                <KpiTile testid="marketing-kpi-pending" label="Pending" value={`₹${fmtINR(dashboard.totals.pending_amount)}`} sub={`${dashboard.totals.pending_count} awaiting`} icon={Clock} accent="amber" />
              </div>
            )}
          </>
        )}

        {meInfo?.linked && (tab === 'referrals' || tab === 'payments') && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  data-testid="marketing-search-input"
                  placeholder="Search patient, OP or referral ID..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-900/60 border border-slate-800 rounded-xl text-sm text-white"
                />
              </div>
              {tab === 'referrals' && (
                <div className="flex items-center bg-slate-900/60 border border-slate-800 rounded-xl p-1" data-testid="marketing-payment-filter">
                  {['all', 'pending', 'paid'].map(p => (
                    <button
                      key={p}
                      onClick={() => setPaymentFilter(p)}
                      data-testid={`marketing-pf-${p}`}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg ${paymentFilter === p ? 'bg-teal-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}
                    >
                      {p[0].toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {tab === 'referrals' && (
              <ReferralTable rows={refs} onOpen={setOpenRefId} testId="marketing-referrals-table" />
            )}
            {tab === 'payments' && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Pending ({paymentSplit.pending.length})</h3>
                  <ReferralTable rows={paymentSplit.pending} onOpen={setOpenRefId} testId="marketing-payments-pending-table" compact />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Paid ({paymentSplit.paid.length})</h3>
                  <ReferralTable rows={paymentSplit.paid} onOpen={setOpenRefId} testId="marketing-payments-paid-table" compact showPaidDate />
                </div>
              </div>
            )}
          </div>
        )}

        {meInfo?.linked && tab === 'reports' && (
          <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-teal-400" /> Download Excel Report
              </h3>
              <p className="text-xs text-slate-400 mt-1">Download an Excel report of your referrals for the selected date range and status filter.</p>
            </div>
            <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl p-1 w-fit" data-testid="marketing-report-pf">
              {['all', 'pending', 'paid'].map(p => (
                <button key={p} onClick={() => setPaymentFilter(p)}
                  data-testid={`marketing-report-pf-${p}`}
                  className={`px-4 py-2 text-xs font-medium rounded-lg ${paymentFilter === p ? 'bg-teal-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
                  {p === 'all' ? 'All Referrals' : p === 'pending' ? 'Pending Only' : 'Paid Only'}
                </button>
              ))}
            </div>
            <button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="marketing-download-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 font-semibold rounded-xl transition-all"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download .xlsx
            </button>
          </div>
        )}

        {openRefId && (
          <ReferralDetailModal referralId={openRefId} onClose={() => setOpenRefId(null)} getAuthHeader={getAuthHeader} />
        )}
      </main>
    </div>
  );
}

function ReferralTable({ rows, onOpen, testId, compact, showPaidDate }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-950/60">
          <tr>
            {!compact && <th className="text-left px-3 py-2">Ref ID</th>}
            <th className="text-left px-3 py-2">Date</th>
            <th className="text-left px-3 py-2">Patient</th>
            {!compact && <th className="text-left px-3 py-2">OP #</th>}
            <th className="text-left px-3 py-2">Doctor</th>
            {!compact && <th className="text-left px-3 py-2">Consultant</th>}
            <th className="text-right px-3 py-2">Final</th>
            <th className="text-right px-3 py-2">Status</th>
            {showPaidDate && <th className="text-right px-3 py-2">Paid Date</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800/70">
          {rows.length === 0 && (
            <tr><td colSpan={compact ? 5 : 8} className="px-3 py-6 text-center text-slate-500">No referrals in this range.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-800/40 cursor-pointer" onClick={() => onOpen(r.id)} data-testid={`marketing-row-${r.referral_id}`}>
              {!compact && <td className="px-3 py-2 font-mono text-teal-400 text-[10px]">{r.referral_id}</td>}
              <td className="px-3 py-2 font-mono text-[10px]">{r.referral_date || '—'}</td>
              <td className="px-3 py-2 text-slate-200 font-medium">{r.patient_name}</td>
              {!compact && <td className="px-3 py-2 text-slate-400">{r.op_number}</td>}
              <td className="px-3 py-2 text-slate-300">{r.referral_doctor_name || '—'}</td>
              {!compact && <td className="px-3 py-2 text-slate-500">{r.consultant_name || '—'}</td>}
              <td className="px-3 py-2 text-right font-mono font-semibold text-white">₹{fmtINR(r.final_referral_amount)}</td>
              <td className="px-3 py-2 text-right">
                <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold border ${r.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' : 'bg-amber-500/10 text-amber-300 border-amber-500/20'}`}>
                  {r.payment_status.toUpperCase()}
                </span>
              </td>
              {showPaidDate && <td className="px-3 py-2 text-right font-mono text-[10px] text-slate-500">{r.payment_date || '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
